import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';
import ReactMarkdown from 'react-markdown';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-night-900 via-night-800 to-night-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center text-launch-blue hover:text-launch-purple transition-colors mb-8"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back to Blog
            </Link>

            <div className="flex items-center gap-4 text-sm text-offwhite/60 mb-6">
              <span className="flex items-center">
                <Calendar size={16} className="mr-2" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <span>•</span>
              <span className="flex items-center">
                <Clock size={16} className="mr-2" />
                {post.readTime}
              </span>
              <span>•</span>
              <span>By {post.author}</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange bg-clip-text text-transparent">
                {post.title}
              </span>
            </h1>

            <p className="text-xl text-offwhite/80 mb-6">
              {post.excerpt}
            </p>

            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center text-xs font-mono px-3 py-1 rounded-full bg-night-700 text-launch-blue border border-launch-blue/30"
                >
                  <Tag size={12} className="mr-1" />
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20 bg-night-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.article
            className="prose prose-lg prose-invert max-w-none
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h1:text-4xl prose-h1:mb-8 prose-h1:mt-16 prose-h1:first:mt-0 prose-h1:bg-gradient-to-r prose-h1:from-launch-blue prose-h1:to-launch-purple prose-h1:bg-clip-text prose-h1:text-transparent
              prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-16 prose-h2:first:mt-0 prose-h2:text-launch-blue prose-h2:border-b prose-h2:border-night-700 prose-h2:pb-4
              prose-h3:text-2xl prose-h3:mb-5 prose-h3:mt-12 prose-h3:text-launch-purple
              prose-h4:text-xl prose-h4:mb-4 prose-h4:mt-10 prose-h4:text-offwhite
              prose-p:text-offwhite/90 prose-p:leading-[1.85] prose-p:mb-8 prose-p:text-[17px]
              prose-a:text-launch-blue prose-a:no-underline prose-a:font-medium hover:prose-a:text-launch-purple prose-a:transition-colors prose-a:border-b prose-a:border-launch-blue/30 hover:prose-a:border-launch-purple/50
              prose-strong:text-white prose-strong:font-semibold
              prose-em:text-offwhite/80 prose-em:italic
              prose-code:text-launch-orange prose-code:bg-night-800 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-[''] prose-code:after:content-['']
              prose-pre:bg-night-800 prose-pre:border prose-pre:border-night-700 prose-pre:rounded-xl prose-pre:p-6 prose-pre:my-10 prose-pre:overflow-x-auto prose-pre:shadow-xl
              prose-ul:my-8 prose-ul:space-y-3 prose-ul:pl-6
              prose-ol:my-8 prose-ol:space-y-3 prose-ol:pl-6
              prose-li:text-offwhite/90 prose-li:leading-[1.8] prose-li:text-[17px] prose-li:pl-2
              prose-li::marker:text-launch-blue prose-li::marker:font-bold
              prose-blockquote:border-l-4 prose-blockquote:border-launch-blue prose-blockquote:pl-8 prose-blockquote:pr-6 prose-blockquote:italic prose-blockquote:text-offwhite/80 prose-blockquote:my-10 prose-blockquote:py-4 prose-blockquote:bg-night-800/50 prose-blockquote:rounded-r-xl
              prose-hr:border-night-700 prose-hr:my-16
              prose-img:rounded-xl prose-img:shadow-2xl prose-img:my-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="text-4xl font-bold mt-16 mb-8 first:mt-0 bg-gradient-to-r from-launch-blue to-launch-purple bg-clip-text text-transparent">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-3xl font-bold mt-16 mb-6 first:mt-0 text-launch-blue border-b border-night-700 pb-4">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-2xl font-bold mt-12 mb-5 text-launch-purple">
                    {children}
                  </h3>
                ),
                h4: ({ children }) => (
                  <h4 className="text-xl font-bold mt-10 mb-4 text-offwhite">
                    {children}
                  </h4>
                ),
                p: ({ children }) => (
                  <p className="text-[17px] text-offwhite/90 leading-[1.85] mb-8">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="my-8 space-y-3 pl-6 list-disc marker:text-launch-blue marker:text-lg">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="my-8 space-y-3 pl-6 list-decimal marker:text-launch-blue marker:font-bold">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="text-[17px] text-offwhite/90 leading-[1.8] pl-2">
                    {children}
                  </li>
                ),
                strong: ({ children }) => (
                  <strong className="text-white font-semibold">
                    {children}
                  </strong>
                ),
                em: ({ children }) => (
                  <em className="text-offwhite/80 italic">
                    {children}
                  </em>
                ),
                a: ({ children, href }) => (
                  <a 
                    href={href} 
                    className="text-launch-blue hover:text-launch-purple transition-colors font-medium border-b border-launch-blue/30 hover:border-launch-purple/50"
                  >
                    {children}
                  </a>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="relative my-10 pl-8 pr-6 py-4 italic text-offwhite/80 bg-night-800/50 rounded-r-xl border-l-4 border-launch-blue">
                    <div className="absolute -left-1 top-0 bottom-0 w-1 bg-gradient-to-b from-launch-blue to-launch-purple rounded-full"></div>
                    {children}
                  </blockquote>
                ),
                code: ({ children, ...props }) => {
                  const inline = !String(children).includes('\n');
                  if (inline) {
                    return (
                      <code className="text-launch-orange bg-night-800 px-2 py-1 rounded text-sm font-mono" {...props}>
                        {children}
                      </code>
                    );
                  }
                  return (
                    <code className="block bg-night-800 border border-night-700 rounded-xl p-6 my-10 overflow-x-auto shadow-xl" {...props}>
                      {children}
                    </code>
                  );
                },
                hr: ({ children }) => (
                  <hr className="border-night-700 my-16" />
                ),
              }}
            >
              {post.content}
            </ReactMarkdown>
          </motion.article>

          {/* Author Bio */}
          <motion.div
            className="mt-20 p-8 bg-gradient-to-br from-night-800 to-night-900 rounded-2xl border border-night-700 shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-launch-blue to-launch-purple flex items-center justify-center text-2xl font-bold">
                {post.author.charAt(0)}
              </div>
              <div className="flex-grow">
                <h3 className="text-xl font-bold mb-2 text-offwhite">About the Author</h3>
                <p className="text-offwhite/80 mb-4 leading-relaxed">
                  <strong className="text-launch-blue">{post.author}</strong> is a co-founder at Cloud Launch,
                  specializing in modern web development and helping businesses launch their digital presence with cutting-edge technology.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-launch-blue hover:text-launch-purple transition-colors font-medium group"
                >
                  Get in touch 
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Related Posts */}
          <motion.div
            className="mt-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <h3 className="text-2xl font-bold mb-8 text-offwhite">Continue Reading</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {blogPosts
                .filter(p => p.id !== post.id && p.tags.some(tag => post.tags.includes(tag)))
                .slice(0, 2)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="block bg-gradient-to-br from-night-800 to-night-900 rounded-xl p-6 border border-night-700 hover:border-launch-purple/50 transition-all group hover:shadow-[0_8px_30px_rgba(168,85,247,0.2)] hover:-translate-y-1"
                  >
                    <div className="flex items-center gap-3 text-sm text-offwhite/60 mb-4">
                      <Calendar size={14} />
                      <span>{new Date(relatedPost.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}</span>
                      <span>•</span>
                      <Clock size={14} />
                      <span>{relatedPost.readTime}</span>
                    </div>
                    <h4 className="text-lg font-semibold mb-3 text-offwhite group-hover:text-launch-blue transition-colors leading-snug">
                      {relatedPost.title}
                    </h4>
                    <p className="text-offwhite/70 text-sm line-clamp-2 leading-relaxed mb-4">
                      {relatedPost.excerpt}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {relatedPost.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs font-mono px-2 py-1 rounded bg-night-700 text-launch-blue/80"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            className="mt-20 p-10 bg-gradient-to-br from-launch-blue/10 via-launch-purple/10 to-launch-orange/10 rounded-2xl border border-launch-blue/30 text-center relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-launch-blue/5 to-launch-purple/5"></div>
            <div className="relative">
              <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange bg-clip-text text-transparent">
                Ready to Launch Your Project?
              </h3>
              <p className="text-offwhite/80 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                Let's discuss how Cloud Launch can help transform your business with modern, high-performance web solutions.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-gradient-to-r from-launch-blue to-launch-purple text-white font-semibold hover:shadow-[0_0_40px_rgba(59,130,246,0.5)] transition-all hover:scale-105"
              >
                Get Started Today
                <span>→</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
